import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useInView } from 'react-intersection-observer';
import { useNavigate } from 'react-router-dom';
import './App.css';
import { ImageGrid, Image, Modal, ModalContent, CloseButton, ImagePreview, HeaderButton, Video } from './Components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faArrowLeft, faDownload } from '@fortawesome/free-solid-svg-icons';
import logo from './lt-logo.svg';

const PhotoGallery = () => {
  const [mediaItems, setMediaItems] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedMediaUrl, setSelectedMediaUrl] = useState(null);
  const [selectedMediaType, setSelectedMediaType] = useState(null);
  const [totalSize, setTotalSize] = useState(0);
  const [unauthorized, setUnauthorized] = useState(false); // Track 401 errors
  const [isFetching, setIsFetching] = useState(false); // Track fetching state
  const [isEmpty, setIsEmpty] = useState(false);
  const navigate = useNavigate();

  const handleCloseImageModal = () => {
    setShowImageModal(false);
    setSelectedMediaUrl(null);
    setSelectedMediaType(null);
  };

  const openMedia = (url, type) => {
    setSelectedMediaUrl(url);
    setSelectedMediaType(type);
    setShowImageModal(true);
  };

  const handleBackgroundClick = (e) => {
    if (e.target === e.currentTarget) {
      handleCloseImageModal();
    }
  };

  const { ref, inView } = useInView({
    triggerOnce: false,
    threshold: 1.0,
  });

  const fetchMedia = useCallback(async () => {
    if (loading || !hasMore || unauthorized || isFetching || isEmpty) return; // Stop fetching if unauthorized or already fetching
    setLoading(true);
    setIsFetching(true); // Add fetching state

    try {
      const response = await axios.get('https://backend.lt.markoshub.com/api/photos', {
        params: { page, limit: 10 },
        withCredentials: true
      });
      setMediaItems(prevItems => [...prevItems, ...response.data.mediaItems]);
      setHasMore(response.data.hasMore);
      setPage(prevPage => prevPage + 1);

      const totalSizeResponse = await axios.get('https://backend.lt.markoshub.com/api/photos/size', {
        withCredentials: true
      });
      setTotalSize(totalSizeResponse.data.totalSize);
    } catch (error) {
      if (error.response && error.response.status === 401) {
        setUnauthorized(true);
        console.error('Unauthorized access - stopping requests');
      } else if (error.response && error.response.status === 444) {
        setIsEmpty(true);
        console.error('No media found');
      } else {
        console.error('Error fetching media:', error);
      }
    } finally {
      setLoading(false);
      setIsFetching(false); // Reset fetching state
    }
  }, [page, hasMore, loading, unauthorized, isFetching, isEmpty]);

  useEffect(() => {
    if (inView && hasMore) {
      fetchMedia();
    }
  }, [inView, fetchMedia, hasMore]);

  const handleDownloadAllPhotos = () => {
    window.location.href = 'https://backend.lt.markoshub.com/api/download-all-photos';
  };

  return (
    <div>
      <header>
        <HeaderButton onClick={() => navigate('/')} className="back-button">
          <FontAwesomeIcon icon={faArrowLeft} />
        </HeaderButton>
        <img src={logo} className="A&M" alt="logo" onClick={() => navigate('/')} width={100} height={100} />
      </header>
      <div className="photo-gallery">
        {mediaItems.length === 0 ? (
          unauthorized ? (
            <p>Na zobrazenie fotiek musíte byť prihlásení. <button onClick={() => navigate('/')} style={{ background: 'none', border: 'none', color: 'blue', textDecoration: 'underline', cursor: 'pointer', padding: 0 }}>Skúste obnoviť stránku</button></p>
          ) : (
            <p>Žiadne fotky neboli nájdené.</p>
          )
        ) : (
          <div>
            <ImageGrid>
              {mediaItems.map((item, index) => (
                item.type === 'photo' ? (
                  <Image
                    key={index}
                    src={`https://backend.lt.markoshub.com${item.thumbnail}`}
                    alt="Gallery"
                    onClick={() => openMedia(`https://backend.lt.markoshub.com${item.fullSize}`, 'photo')}
                  />
                ) : (
                  <Video
                    key={index}
                    src={`https://backend.lt.markoshub.com${item.fullSize}#t=0.001`}
                    controls
                    style={{ width: '100%', height: 'auto' }}
                  />
                )
              ))}
            </ImageGrid>
            <button className="download-button" onClick={handleDownloadAllPhotos}>
              <FontAwesomeIcon icon={faDownload} /> Stiahnuť všetky fotky
            </button>
            <p>Celková veľkosť: {Math.round(totalSize / (1024 * 1024))} MB</p>
          </div>
        )}
        {showImageModal && (
          <Modal onClick={handleBackgroundClick}>
            <ModalContent>
              <CloseButton onClick={handleCloseImageModal}>
                <FontAwesomeIcon icon={faTimes} />
              </CloseButton>
              {selectedMediaType === 'photo' ? (
                <ImagePreview
                  src={selectedMediaUrl}
                  alt="Selected"
                  style={{ width: '100%', height: 'auto' }}
                />
              ) : (
                <video
                  src={selectedMediaUrl}
                  controls
                  style={{ width: '100%', height: 'auto' }}
                />
              )}
            </ModalContent>
          </Modal>
        )}
        {loading && <p>Načítavam ďalšie fotky</p>}
        <div ref={ref} />
      </div>
    </div>
  );
};

export default PhotoGallery;
